// @file Network requests related to things on the padlet page
/* eslint-disable no-return-await, no-template-curly-in-string */
import { asciiSafeStringify } from '@@/bits/json_stringify'
import {
  Comment as CommentType,
  CommentsFetchQuery,
  fetchComments,
  fetchReactions,
  fetchUser,
  fetchWishes,
  JsonAPIResource,
  JsonAPIResponse,
  Reaction as ReactionType,
  ReactionsFetchQuery,
  User as UserType,
  Wall as WallType,
  WallBackgroundType,
  Wish as WishType,
  WishesFetchQuery,
} from '@padlet/arvo'
import { FetchOptions, HTTPMethod } from '@padlet/fetch'
import { FetchOptionReferrer } from '@padlet/fetch/lib/types'
import { template } from 'lodash-es'
import { fetchJson, fetchJsonWithRetries } from 'surface/api_fetch'
import type {
  AccumulatedReactions,
  CanSetWallPrivacyOptions,
  Id,
  LibraryId,
  MagicWallCreationResult,
  Plan as PlanType,
  UserMentionSuggestion,
  UserMentionSuggestionAttributes,
  WallBackground,
} from 'types'

interface VersionedFetchOptions extends FetchOptions {
  apiVersion: number
}

const API_VERSION = 5
const VIA = 'webapp'

class FetchableObject {
  public static get url(): string {
    return ''
  }

  public static get fetchUrl(): string {
    return ''
  }

  public static buildUrl(url, options): string {
    return template(url)({ ...options, interpolate: /\${([\s\S]+?)}/g })
  }

  public static async readAll(options, fetchOptions = {}): Promise<any> {
    return await fetchJson(this.buildUrl(this.fetchUrl, options), {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async read(obj, fetchOptions = {}): Promise<any> {
    return await fetchJson(`${this.url}/${obj.id}`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async update(obj, fetchOptions = {}): Promise<any> {
    return await fetchJson(`${this.url}/${obj.id}`, {
      method: HTTPMethod.put,
      body: asciiSafeStringify(obj),
      ...fetchOptions,
    })
  }

  public static async create(obj, fetchOptions = {}): Promise<any> {
    return await fetchJson(`${this.url}`, {
      method: HTTPMethod.post,
      body: asciiSafeStringify(obj),
      ...fetchOptions,
    })
  }

  public static async delete(obj, fetchOptions = {}): Promise<any> {
    return await fetchJson(`${this.url}/${obj.id}`, {
      method: HTTPMethod.delete,
      ...fetchOptions,
    })
  }
}

class Reaction extends FetchableObject {
  public static async fetchAccumulatedReactions({
    wallId,
  }: {
    wallId: number
  }): Promise<JsonAPIResponse<AccumulatedReactions>> {
    return await fetchJson(`/api/${API_VERSION}/accumulated_reactions?wall_id=${wallId}`, {
      method: HTTPMethod.get,
    })
  }

  public static get url(): string {
    return `/api/${API_VERSION}/reactions`
  }

  public static get fetchUrl(): string {
    return `/api/${API_VERSION}/reactions?wall_id=\${ wall.id }`
  }

  public static async fetch(query: ReactionsFetchQuery): Promise<JsonAPIResponse<ReactionType>> {
    return await fetchReactions(query)
  }
}

class PostConnection extends FetchableObject {
  public static get url(): string {
    return `/api/${API_VERSION}/wish_connections`
  }

  public static get fetchUrl(): string {
    return `/api/${API_VERSION}/wish_connections?wall_id=\${ wall.id }`
  }
}

class Comment extends FetchableObject {
  public static get url(): string {
    return `/api/${API_VERSION}/comments`
  }

  public static get fetchUrl(): string {
    return `/api/${API_VERSION}/comments?wall_id=\${ wall.id }`
  }

  public static async fetch(query: CommentsFetchQuery): Promise<JsonAPIResponse<CommentType>> {
    return await fetchComments(query)
  }
}

class WallSection extends FetchableObject {
  public static get url(): string {
    return `/api/${API_VERSION}/wall_sections`
  }

  public static get fetchUrl(): string {
    return `/api/${API_VERSION}/wall_sections?wall_id=\${ wall.id }`
  }
}

// TODO: move to @padlet/arvo
class WallTableLayout extends FetchableObject {
  private static readonly rowsEndpoint = '/api/wall_table_layout/rows'
  private static readonly colsEndpoint = '/api/wall_table_layout/cols'
  private static readonly rowAndColEndpoint = '/api/wall_table_layout/row_and_col'

  public static get fetchUrl(): string {
    return '/api/wall_table_layout?wall_id=${ wall.id }'
  }

  public static async createRow(obj): Promise<any> {
    return await fetchJson(this.rowsEndpoint, {
      method: HTTPMethod.post,
      body: asciiSafeStringify(obj),
    })
  }

  public static async createCol(obj): Promise<any> {
    return await fetchJson(this.colsEndpoint, {
      method: HTTPMethod.post,
      body: asciiSafeStringify(obj),
    })
  }

  public static async createRowAndCol(obj): Promise<any> {
    return await fetchJson(this.rowAndColEndpoint, {
      method: HTTPMethod.post,
      body: asciiSafeStringify(obj),
    })
  }

  public static async updateRow(obj): Promise<any> {
    return await fetchJson(`${this.rowsEndpoint}/${obj.id}`, {
      method: HTTPMethod.put,
      body: asciiSafeStringify(obj),
    })
  }

  public static async updateCol(obj): Promise<any> {
    return await fetchJson(`${this.colsEndpoint}/${obj.id}`, {
      method: HTTPMethod.put,
      body: asciiSafeStringify(obj),
    })
  }

  public static async deleteRow(obj): Promise<any> {
    return await fetchJson(`${this.rowsEndpoint}/${obj.id}`, {
      method: HTTPMethod.delete,
    })
  }

  public static async deleteCol(obj): Promise<any> {
    return await fetchJson(`${this.colsEndpoint}/${obj.id}`, {
      method: HTTPMethod.delete,
    })
  }

  public static async putPostInCell(obj): Promise<any> {
    return await fetchJson('/api/wall_table_layout/cell', {
      method: HTTPMethod.post,
      body: asciiSafeStringify(obj),
    })
  }
}

class Wall extends FetchableObject {
  public static get url(): string {
    return '/walls'
  }

  private static get convertLayoutUrl(): string {
    return '/walls/${ id }/layout'
  }

  public static async convertLayout({ id, newLayout }, fetchOptions = {}): Promise<any> {
    return await fetchJson(this.buildUrl(this.convertLayoutUrl, { id }), {
      method: HTTPMethod.put,
      body: asciiSafeStringify({ type: newLayout }),
      ...fetchOptions,
    })
  }

  public static async create(
    { viz, libraryId, groupBySection }: { viz: string; libraryId?: LibraryId; groupBySection?: boolean },
    fetchOptions = {},
  ): Promise<any> {
    const wishArrangement = groupBySection === true ? { wish_arrangement: { group_by: 'section' } } : {}
    return await fetchJson(`/api/${API_VERSION}/walls`, {
      method: HTTPMethod.post,
      body: asciiSafeStringify({ viz, via: VIA, library_id: libraryId, ...wishArrangement }),
      ...fetchOptions,
    })
  }

  public static async createMagic(
    { role, userPrompt, postsNumber }: { role: string; userPrompt: string; postsNumber: string },
    fetchOptions = {},
  ): Promise<JsonAPIResponse<MagicWallCreationResult>> {
    return await fetchJson(`/api/${API_VERSION}/walls/magic_wall`, {
      method: HTTPMethod.post,
      body: asciiSafeStringify({
        role,
        user_prompt: userPrompt,
        posts_number: postsNumber,
      }),
      ...fetchOptions,
    })
  }

  public static async archive(wallId: number): Promise<JsonAPIResponse<WallType>> {
    return await fetchJson(`/api/${API_VERSION}/walls/${wallId}/archive`, {
      method: HTTPMethod.put,
    })
  }
}

class Wish extends FetchableObject {
  public static get url(): string {
    return `/api/${API_VERSION}/wishes`
  }

  public static get fetchSingleUrl(): string {
    return `/api/${API_VERSION}/wishes/\${ wish_id }`
  }

  public static get fetchUrl(): string {
    return `/api/${API_VERSION}/wishes?wall_id=\${ wall.id }&page_start=\${ pageStart }`
  }

  public static get transferUrl(): string {
    return `/api/${API_VERSION}/wishes/\${ wish_id }/transfer`
  }

  public static get copyUrl(): string {
    return `/api/${API_VERSION}/wishes/\${ wish_id }/copy`
  }

  public static get approveUrl(): string {
    return `/api/${API_VERSION}/wishes/\${ wish_id }/approve`
  }

  public static async fetchSingle({ wishId }, fetchOptions = {}): Promise<JsonAPIResponse<WishType>> {
    return await fetchJson(this.buildUrl(this.fetchSingleUrl, { wish_id: wishId }), {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async fetch(query: WishesFetchQuery): Promise<JsonAPIResponse<WishType>> {
    return await fetchWishes(query)
  }

  public static async transfer(
    { wishId, wallId, wallSectionId, wallTitle, wallDescription, libraryId },
    fetchOptions = {},
  ): Promise<any> {
    return await fetchJson(this.buildUrl(this.transferUrl, { wish_id: wishId }), {
      method: HTTPMethod.put,
      body: asciiSafeStringify({
        wall_id: wallId,
        wall_section_id: wallSectionId,
        wall_title: wallTitle,
        wall_description: wallDescription,
        library_id: libraryId,
      }),
      ...fetchOptions,
    })
  }

  public static async copy(
    { wishId, wallId, wallSectionId, wallTitle, wallDescription, postPosition, libraryId },
    fetchOptions = {},
  ): Promise<any> {
    return await fetchJson(this.buildUrl(this.copyUrl, { wish_id: wishId }), {
      method: HTTPMethod.post,
      body: asciiSafeStringify({
        wall_id: wallId,
        wall_section_id: wallSectionId,
        wall_title: wallTitle,
        wall_description: wallDescription,
        library_id: libraryId,
        ...postPosition,
      }),
      ...fetchOptions,
    })
  }

  public static async approve({ wishId }, fetchOptions = {}): Promise<any> {
    return await fetchJson(this.buildUrl(this.approveUrl, { wish_id: wishId }), {
      method: HTTPMethod.put,
      body: asciiSafeStringify({}),
      ...fetchOptions,
    })
  }

  public static async update(obj, fetchOptions = {}): Promise<any> {
    return await fetchJsonWithRetries(`${this.url}/${obj.id}`, {
      method: HTTPMethod.put,
      body: asciiSafeStringify(obj),
      ...fetchOptions,
    })
  }
}

// UserContributor is read only
class UserContributor extends FetchableObject {
  public static get url(): string {
    return `/api/${API_VERSION}/users`
  }

  // The API WallContributorIds but it only return post author ids to match old behaviour for now
  public static async readWallContributorIds(wall, fetchOptions = {}): Promise<any> {
    return await fetchJson(`/api/${API_VERSION}/walls/${wall.id}/contributors`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async read(userId: number): Promise<JsonAPIResponse<UserType>> {
    return await fetchUser(userId)
  }
}

/**
 * @example PadletApi.ContributingStatus.update({wall_id: 1, status: 1})
 */
class ContributingStatus extends FetchableObject {
  public static get url(): string {
    return `/api/${API_VERSION}/contributing_status`
  }

  public static get pingUrl(): string {
    return `/api/${API_VERSION}/contributing_status/ping`
  }

  public static async ping(obj, fetchOptions = {}): Promise<any> {
    return await fetchJson(`${this.pingUrl}`, {
      method: HTTPMethod.post,
      body: asciiSafeStringify(obj),
      ...fetchOptions,
      referrer: document.location.href as FetchOptionReferrer,
    })
  }

  public static async update(obj, fetchOptions = {}): Promise<any> {
    return await fetchJson(`${this.url}`, {
      method: HTTPMethod.put,
      body: asciiSafeStringify(obj),
      ...fetchOptions,
    })
  }
}

class BrahmsToken extends FetchableObject {
  public static get url(): string {
    return `/api/${API_VERSION}/brahms_token`
  }

  public static async get(obj, fetchOptions = {}): Promise<any> {
    return await fetchJson(`${this.url}`, {
      method: HTTPMethod.post,
      body: asciiSafeStringify(obj),
      ...fetchOptions,
      referrer: document.location.href as FetchOptionReferrer,
    })
  }
}

class Plan {
  /**
   * Fetch list of available plans for current user (respective to the user's country code/currency)
   */
  public static async fetch(fetchOptions = {}): Promise<JsonAPIResponse<PlanType>> {
    return await fetchJson(`/api/${API_VERSION}/plans`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }
}

const collaboratorLeavePadlet = async (wallId: Id, fetchOptions = {}): Promise<any> => {
  return await fetchJson(`/api/${API_VERSION}/wall_collaborators/leave`, {
    method: HTTPMethod.post,
    body: asciiSafeStringify({
      wall_id: wallId,
    }),
    ...fetchOptions,
  })
}

class Library extends FetchableObject {
  public static get libraryUrl(): string {
    return `/api/1/libraries?userId=\${ userId }&filter=\${ filter }`
  }

  public static async fetchCreatableLibraries({ userId }, fetchOptions = {}): Promise<JsonAPIResponse<Library>> {
    return await fetchJson(this.buildUrl(this.libraryUrl, { userId, filter: 'wall_creatable' }), {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async fetchViewableLibraries({ userId }, fetchOptions = {}): Promise<JsonAPIResponse<Library>> {
    return await fetchJson(this.buildUrl(this.libraryUrl, { userId, filter: 'all_walls_viewable' }), {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }
}

class Wallpaper extends FetchableObject {
  private static get backgroundsUrl(): string {
    return `/api/\${ apiVersion }/backgrounds/\${category}`
  }

  public static get routeMap(): Record<WallBackgroundType, string> {
    return {
      picture: 'pictures',
      solid_color: 'solid_colors',
      gradient: 'gradients',
      pattern: 'patterns',
      art_illustrations: 'art_illustrations',
    }
  }

  public static async fetchBackgrounds(
    { category }: { category: WallBackgroundType },
    fetchOptions = { apiVersion: 7 },
  ): Promise<WallBackground[]> {
    const { apiVersion, ...restFetchOptions } = fetchOptions
    const { data }: JsonAPIResponse<WallBackground> = await fetchJson(
      this.buildUrl(this.backgroundsUrl, { category: this.routeMap[category], apiVersion }),
      {
        method: HTTPMethod.get,
        ...restFetchOptions,
      },
    )

    if (data != null && Array.isArray(data)) {
      return data.map((jsonResource: JsonAPIResource<WallBackground>): WallBackground => jsonResource.attributes)
    } else {
      return []
    }
  }
}

class WallPrivacyOptions extends FetchableObject {
  public static get wallPrivacyOptionsUrl(): string {
    return `/api/\${ apiVersion }/surface/walls/\${ wallId }/privacy_options`
  }

  public static async fetchWallPrivacyOptions(
    wallId,
    fetchOptions = { apiVersion: 1 },
  ): Promise<JsonAPIResponse<CanSetWallPrivacyOptions>> {
    const { apiVersion, ...restFetchOptions } = fetchOptions

    return await fetchJson(this.buildUrl(this.wallPrivacyOptionsUrl, { wallId, apiVersion }), {
      method: HTTPMethod.get,
      ...restFetchOptions,
    })
  }
}

class MentionSuggestions extends FetchableObject {
  public static async fetchMentionSuggestions(
    params: { wallId: number; wishId?: number; q?: string },
    fetchOptions: VersionedFetchOptions = { apiVersion: 1 },
  ): Promise<UserMentionSuggestion[]> {
    const { apiVersion, ...otherFetchOptions } = fetchOptions
    const query: FetchOptions['query'] = {
      wallId: params.wallId.toString(),
    }
    if (params.wishId != null) query.wishId = params.wishId.toString()
    if (params.q != null) query.q = params.q
    const response: JsonAPIResponse<UserMentionSuggestionAttributes> = await fetchJson(
      this.buildUrl(this.mentionSuggestionsUrl, { apiVersion }),
      {
        ...otherFetchOptions,
        method: HTTPMethod.get,
        query,
      },
    )
    if (!Array.isArray(response.data)) {
      throw new Error('Mention suggestions should be an array')
    }
    const suggestions: UserMentionSuggestion[] = response.data.map(({ type, attributes }) => ({
      type,
      attributes,
    }))
    return suggestions
  }

  private static get mentionSuggestionsUrl(): string {
    return `/api/\${ apiVersion }/mention_suggestions`
  }
}

export default {
  Reaction,
  PostConnection,
  Comment,
  WallSection,
  WallTableLayout,
  Wall,
  Wish,
  UserContributor,
  ContributingStatus,
  BrahmsToken,
  Plan,
  collaboratorLeavePadlet,
  Library,
  Wallpaper,
  WallPrivacyOptions,
  MentionSuggestions,
}
